import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, CalendarClock, CalendarRange } from 'lucide-react';
import {
  BOARD_GROUP_PREVISTO_HOJE,
  BOARD_GROUP_PREVISTAS_AMANHA,
  BOARD_GROUP_FUTURA,
} from '../constants/boardGroups';
import { getBoardOrderDragSource } from '../utils/boardOrderDrag';
import './TechnicianColumnSessionDropOverlay.css';

const ZONES = [
  {
    groupName: BOARD_GROUP_PREVISTO_HOJE,
    label: 'Previsto para hoje',
    short: 'Hoje',
    accent: 'hoje',
    Icon: Calendar,
  },
  {
    groupName: BOARD_GROUP_PREVISTAS_AMANHA,
    label: 'Previstas para amanhã',
    short: 'Amanhã',
    accent: 'amanha',
    Icon: CalendarClock,
  },
  {
    groupName: BOARD_GROUP_FUTURA,
    label: 'Futuro',
    short: 'Futuro',
    accent: 'futura',
    Icon: CalendarRange,
  },
];

/** Altura mínima confortável para 3 zonas + hint + gaps dentro da coluna. */
const COMPACT_HEIGHT_THRESHOLD = 280;
const FLOAT_MIN_HEIGHT = 292;
const VIEWPORT_MARGIN = 8;

function normalizeTechName(name) {
  return String(name || '')
    .trim()
    .toLowerCase();
}

/**
 * Overlay de drop por sessão (Hoje / Amanhã / Futura).
 * Em coluna baixa (empilhada/resizable), porta as zonas para fora da área do técnico (fixed).
 */
function TechnicianColumnSessionDropOverlay({
  armed = false,
  activeZone,
  onZoneDragEnter,
  onZoneDragLeave,
  onZoneDrop,
  /** ref do .column-content (host da coluna) */
  anchorRef = null,
  /** id estável da coluna (técnico) — identifica o painel flutuante no dragleave */
  anchorId = '',
  /** true quando meia-coluna empilhada — limiar um pouco maior */
  preferFloat = false,
  /** mantém o drop armado ao interagir no painel flutuante */
  onKeepArmed,
  /** desarma ao sair do painel flutuante e da coluna */
  onDisarm,
}) {
  const inlineRootRef = useRef(null);
  const floatRootRef = useRef(null);
  const [floatOut, setFloatOut] = useState(false);
  const [floatStyle, setFloatStyle] = useState(null);
  const floatAttrId = encodeURIComponent(String(anchorId || 'tech'));
  const dragSource = getBoardOrderDragSource();
  const sourceTech = normalizeTechName(dragSource?.fromTechnicianName);
  const sourceGroup = String(dragSource?.fromGroup || '').trim();
  const columnTech = normalizeTechName(anchorId);
  const isSameTechnicianDrag =
    Boolean(sourceTech) && Boolean(columnTech) && sourceTech === columnTech;

  const measure = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor || !armed) {
      setFloatOut(false);
      setFloatStyle(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const threshold = preferFloat ? COMPACT_HEIGHT_THRESHOLD + 40 : COMPACT_HEIGHT_THRESHOLD;
    const needsFloat = rect.height < threshold;
    if (!needsFloat) {
      setFloatOut(false);
      setFloatStyle(null);
      return;
    }

    const width = Math.max(rect.width, 200);
    let left = rect.left;
    let height = Math.max(rect.height, FLOAT_MIN_HEIGHT);
    let top = rect.top;

    // Preferir expandir para baixo a partir do topo da coluna; se não couber, sobe.
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - height);
    }
    if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - width);
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;

    setFloatOut(true);
    setFloatStyle({
      position: 'fixed',
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      width: `${Math.round(width)}px`,
      height: `${Math.round(height)}px`,
      zIndex: 12050,
    });
  }, [anchorRef, armed, preferFloat]);

  useLayoutEffect(() => {
    measure();
  }, [measure, armed, preferFloat, activeZone]);

  useEffect(() => {
    if (!armed) return undefined;
    const onWin = () => measure();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    const anchor = anchorRef?.current;
    let ro;
    if (anchor && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onWin);
      ro.observe(anchor);
    }
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
      ro?.disconnect();
    };
  }, [armed, measure, anchorRef]);

  const handleFloatDragLeave = (e) => {
    const related = e.relatedTarget;
    if (related && floatRootRef.current?.contains(related)) return;
    if (related && anchorRef?.current?.contains(related)) return;
    // Saiu do painel flutuante e não entrou na coluna host
    const { clientX: x, clientY: y } = e;
    const anchor = anchorRef?.current;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        onKeepArmed?.();
        return;
      }
    }
    onDisarm?.();
  };

  const zones = (
    <>
      <p className="tech-session-drop-overlay-hint">Solte na sessão desejada</p>
      <div className="tech-session-drop-zones">
        {ZONES.map(({ groupName, label, short, accent, Icon }) => {
          const isActive = activeZone === groupName;
          const isSameSession =
            isSameTechnicianDrag && sourceGroup && sourceGroup === groupName;
          const isKeep = isActive && isSameSession;
          const ctaText = isKeep ? 'Mantenha aqui' : isActive ? 'Solte aqui' : 'Arraste para cá';
          return (
            <div
              key={groupName}
              className={[
                'tech-session-drop-zone',
                `tech-session-drop-zone--${accent}`,
                isKeep ? 'tech-session-drop-zone--keep' : '',
                isActive && !isKeep ? 'tech-session-drop-zone--active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onKeepArmed?.();
                onZoneDragEnter?.(groupName);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = isSameSession ? 'none' : 'move';
                onKeepArmed?.();
                onZoneDragEnter?.(groupName);
              }}
              onDragLeave={(e) => {
                const related = e.relatedTarget;
                if (!related || !e.currentTarget.contains(related)) {
                  onZoneDragLeave?.();
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onZoneDrop?.(e, groupName);
              }}
            >
              <span className="tech-session-drop-zone-icon" aria-hidden>
                <Icon size={20} strokeWidth={2} />
              </span>
              <span className="tech-session-drop-zone-label">{label}</span>
              <span className="tech-session-drop-zone-short">{short}</span>
              <span className="tech-session-drop-zone-cta">{ctaText}</span>
            </div>
          );
        })}
      </div>
    </>
  );

  const inlineOverlay = (
    <div
      ref={inlineRootRef}
      className={[
        'tech-session-drop-overlay',
        armed && !floatOut ? 'tech-session-drop-overlay--armed' : '',
        floatOut && armed ? 'tech-session-drop-overlay--anchor-only' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="presentation"
      aria-hidden={!armed || floatOut}
      data-tech-session-drop-anchor=""
    >
      {/* Em float-out o painel real é o portal; âncora só marca a coluna e recebe drag. */}
      {!floatOut ? zones : null}
    </div>
  );

  const floatingOverlay =
    armed && floatOut && floatStyle && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={floatRootRef}
            className="tech-session-drop-overlay tech-session-drop-overlay--armed tech-session-drop-overlay--floating"
            role="presentation"
            aria-hidden={false}
            data-tech-session-drop-floating={floatAttrId}
            style={floatStyle}
            onDragEnter={(e) => {
              e.preventDefault();
              onKeepArmed?.();
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              onKeepArmed?.();
            }}
            onDragLeave={handleFloatDragLeave}
          >
            {zones}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {inlineOverlay}
      {floatingOverlay}
    </>
  );
}

export default memo(TechnicianColumnSessionDropOverlay);
